import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, SupportAgent as SupportAgentIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { startPartnerSupportSession } from '../utils/partnerSupportSession';
import DocumentPageOverlay, { documentOverlayActionSx } from './DocumentPageOverlay';
import PublicPackageDetails from '../pages/PublicPackageDetails';

const STATUS_COLORS = {
  published: 'success',
  draft: 'warning',
  archived: 'default',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const AdminPartnerPackagesDialog = ({ open, partner, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startingFor, setStartingFor] = useState(null);
  const [previewPkg, setPreviewPkg] = useState(null);

  const partnerId = partner?.id;

  const loadPackages = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/admin/partners/${partnerId}/packages`);
      setPackages(Array.isArray(response.data?.packages) ? response.data.packages : []);
    } catch (err) {
      console.error('Failed to load partner packages:', err);
      setError(t('partnerSupport.loadFailed'));
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [partnerId, t]);

  useEffect(() => {
    if (open) loadPackages();
  }, [open, loadPackages]);

  const openAsPartner = useCallback(async (path, key) => {
    setStartingFor(key);
    setError('');
    try {
      await startPartnerSupportSession(partnerId);
      setPreviewPkg(null);
      onClose?.();
      navigate(path);
    } catch (err) {
      console.error('Failed to start support session:', err);
      setError(t('partnerSupport.startFailed'));
    } finally {
      setStartingFor(null);
    }
  }, [navigate, onClose, partnerId, t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="admin-partner-packages-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
        {t('partnerSupport.dialogTitle', { company: partner?.companyName || '' })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, mb: 2 }}>
          {t('partnerSupport.dialogHelp')}
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : packages.length === 0 ? (
          <Typography sx={{ color: DOCUMENT.ink3, py: 2 }}>{t('partnerSupport.empty')}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('partnerSupport.colPackage')}</TableCell>
                <TableCell>{t('partnerSupport.colStatus')}</TableCell>
                <TableCell>{t('partnerSupport.colVisibility')}</TableCell>
                <TableCell>{t('partnerSupport.colUpdated')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} data-testid={`admin-partner-package-${pkg.id}`}>
                  <TableCell>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setPreviewPkg(pkg)}
                      disabled={!pkg.slug}
                      data-testid={`admin-partner-package-preview-${pkg.id}`}
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: DOCUMENT.ink,
                        textAlign: 'left',
                        textDecorationColor: DOCUMENT.ruleHard,
                        '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
                      }}
                    >
                      {pkg.title}
                    </Link>
                    {pkg.destination ? (
                      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{pkg.destination}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`partnerSupport.status.${pkg.status}`, pkg.status)}
                      color={STATUS_COLORS[pkg.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {t(`partnerSupport.visibility.${pkg.visibility || 'marketplace'}`, pkg.visibility || 'marketplace')}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{formatDate(pkg.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={startingFor === pkg.id ? <CircularProgress size={14} /> : <EditIcon />}
                      disabled={Boolean(startingFor)}
                      onClick={() => openAsPartner(`/partner/packages/${pkg.id}/edit`, pkg.id)}
                      data-testid={`admin-partner-package-edit-${pkg.id}`}
                    >
                      {t('partnerSupport.edit')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          startIcon={startingFor === 'portal' ? <CircularProgress size={14} color="inherit" /> : <SupportAgentIcon />}
          disabled={!partnerId || Boolean(startingFor)}
          onClick={() => openAsPartner('/partner/packages', 'portal')}
          data-testid="admin-partner-open-portal"
        >
          {t('partnerSupport.openPortal')}
        </Button>
        <Button onClick={onClose}>{t('partnerSupport.close')}</Button>
      </DialogActions>

      <DocumentPageOverlay
        open={Boolean(previewPkg)}
        onClose={() => setPreviewPkg(null)}
        title={t('partnerLists.preview')}
        testId="admin-package-preview"
        actions={previewPkg ? (
          <Button
            size="small"
            disabled={Boolean(startingFor)}
            onClick={() => openAsPartner(`/partner/packages/${previewPkg.id}/edit`, previewPkg.id)}
            sx={documentOverlayActionSx}
          >
            {t('partnerSupport.edit')}
          </Button>
        ) : null}
      >
        {previewPkg?.slug ? (
          <PublicPackageDetails previewSlug={previewPkg.slug} adminPreviewPartnerId={partnerId} />
        ) : null}
      </DocumentPageOverlay>
    </Dialog>
  );
};

export default AdminPartnerPackagesDialog;
